import React from 'react';
import { ComposableDeviceValues } from '../AddComposableDevice';
import { FormikProps } from 'formik';
import { FormControl, MenuItem } from '@mui/material';
import { getPolyglot } from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/store.model';
import {
  getMachinesTypeSelector,
  getMachineTypeSelector,
} from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { ErrorText } from '../../../../../theme/components/Forms';
import {
  StyledInputLabel,
  StyledSelectField,
  StyledStack,
  StyledTextField,
} from './StyledElements';

export const DeviceForm = ({
  formik,
  displayType = true,
}: {
  formik: FormikProps<ComposableDeviceValues>;
  displayType?: boolean;
}) => {
  const polyglot = getPolyglot();
  const machineTypes = useAppSelector(getMachinesTypeSelector);

  const isEditMode = Boolean(formik.values?.id);
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  const modelTypes = JSON.parse(
    machineType?.labels?.businessModelOptions || '[]'
  ) as { name: string; decksQuantity: number }[];

  return (
    <StyledStack>
      {displayType ? (
        <FormControl>
          <StyledInputLabel htmlFor="machine-type-select">
            {polyglot.t('composable_device.type')}
          </StyledInputLabel>
          <StyledSelectField
            onChange={(event) => {
              formik.handleChange(event);
              formik.setFieldValue(
                'composableDevice.machineTypeId',
                event.target.value
              );
            }}
            value={formik.values.composableDevice?.machineTypeId}
            hiddenLabel={true}
            id="machine-type-select"
            disabled={isEditMode}
          >
            {machineTypes.map((machineType) => (
              <MenuItem key={machineType.id} value={machineType.id}>
                {machineType?.name}
              </MenuItem>
            ))}
          </StyledSelectField>
        </FormControl>
      ) : null}
      <FormControl>
        <StyledInputLabel htmlFor="name-input">
          {polyglot.t('composable_device.name')}*
        </StyledInputLabel>
        <StyledTextField
          name="name"
          type="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          required
          error={Boolean(formik.errors.name)}
          id="name-input"
          placeholder={polyglot.t('composable_device.enter_name')}
        />
        {formik.touched.name && formik.errors.name && (
          <ErrorText fontSize={'0.9rem'}>
            {polyglot.t(formik.errors.name)}
          </ErrorText>
        )}
      </FormControl>
      <FormControl>
        <StyledInputLabel htmlFor="model-select">
          {polyglot.t('composable_device.model')}
        </StyledInputLabel>
        <StyledSelectField
          onChange={(event) => {
            const selectedModel = modelTypes.find(
              (model) => model.name === event.target.value
            );
            formik.setFieldValue(
              'composableDevice.labels.businessModel',
              selectedModel?.name
            );
            formik.setFieldValue(
              'composableDevice.labels.commandersQuantity',
              selectedModel?.decksQuantity
            );
          }}
          value={formik.values.composableDevice?.labels?.businessModel}
          hiddenLabel={true}
          id="model-select"
          name="composableDevice.labels.businessModel"
        >
          {modelTypes.map((modelType) => (
            <MenuItem key={modelType.name} value={modelType.name}>
              {modelType?.name}
            </MenuItem>
          ))}
        </StyledSelectField>
      </FormControl>
      <FormControl>
        <StyledInputLabel htmlFor="description-input">
          {polyglot.t('composable_device.description')}
        </StyledInputLabel>
        <StyledTextField
          id="description-input"
          name="composableDevice.labels.description"
          type="description"
          multiline
          rows={4}
          onChange={formik.handleChange}
          value={formik.values.composableDevice?.labels?.description ?? ''}
          placeholder={polyglot.t('composable_device.enter_description')}
        />
      </FormControl>
    </StyledStack>
  );
};
